import React, { useState } from "react";
import { CheckCircle2, Circle, Eye, Trash2, Loader2 } from "lucide-react";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import toast from "react-hot-toast";
import InvoiceDetailsView from "./InvoiceDetailsView";
import { reSyncInvoiceToClient } from "../services/invoiceSyncService";

function InvoiceList({ invoices, onDelete, formatAmount, globalUser, onRefresh, onEdit, readOnly = false }) {
  const navigate = useNavigate();
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(null);

  const userType = localStorage.getItem("userType");
  const isClient = userType === "client";

  const handleViewInvoice = (invoice) => {
    // Lawyer view opens the full-page invoice screen (CreateInvoice in edit mode).
    // Client (read-only) keeps the lightweight details modal.
    if (readOnly) {
      setSelectedInvoice(invoice);
      setShowDetailsModal(true);
    } else {
      navigate(`/portal/invoice/${invoice.id}`);
    }
  };

  const handleMarkPaid = async (invoice) => {
    if (invoice.status === "paid") return;

    setMarkingPaid(invoice.id);
    try {
      const now = new Date().toISOString();
      const userName = globalUser?.displayName || globalUser?.email || "User";

      const timelineEvent = {
        id: Date.now().toString(),
        type: "marked_as_paid",
        timestamp: now,
        by: userName,
        changes: {
          "Invoice Status": { old: invoice.status, new: "paid" },
          "Marked By": { old: "-", new: isClient ? "Client" : "Lawyer" },
        },
      };

      if (isClient) {
        const sharedInvoiceRef = doc(db, "client", globalUser.uid, "sharedInvoices", invoice.id);
        await updateDoc(sharedInvoiceRef, {
          status: "paid",
          dueAmount: 0,
          markedPaidAt: now,
          markedPaidBy: "client",
          timeline: arrayUnion(timelineEvent),
        });
      } else {
        const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);
        await updateDoc(invoiceRef, {
          status: "paid",
          dueAmount: 0,
          markedPaidAt: now,
          markedPaidBy: "lawyer",
          timeline: arrayUnion(timelineEvent),
        });

        if (invoice.contactId) {
          const lawyerName = globalUser?.displayName || "Lawyer";
          await reSyncInvoiceToClient(globalUser.uid, invoice.id, invoice.contactId, lawyerName);
        }
      }

      toast.success("Invoice marked as paid");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      toast.error("Failed to mark as paid");
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleMarkUnpaid = async (invoice) => {
    if (invoice.status !== "paid") return;

    setMarkingPaid(invoice.id);
    try {
      const now = new Date().toISOString();
      const userName = globalUser?.displayName || globalUser?.email || "User";

      const timelineEvent = {
        id: Date.now().toString(),
        type: "marked_as_unpaid",
        timestamp: now,
        by: userName,
        changes: {
          "Invoice Status": { old: "paid", new: "raised" },
          "Marked By": { old: "-", new: isClient ? "Client" : "Lawyer" },
        },
      };

      if (isClient) {
        const sharedInvoiceRef = doc(db, "client", globalUser.uid, "sharedInvoices", invoice.id);
        await updateDoc(sharedInvoiceRef, {
          status: "raised",
          dueAmount: invoice.totalAmount || 0,
          markedPaidAt: null,
          markedPaidBy: null,
          timeline: arrayUnion(timelineEvent),
        });
      } else {
        const invoiceRef = doc(db, "lawyers", globalUser.uid, "invoices", invoice.id);
        await updateDoc(invoiceRef, {
          status: "raised",
          dueAmount: invoice.totalAmount || 0,
          markedPaidAt: null,
          markedPaidBy: null,
          timeline: arrayUnion(timelineEvent),
        });

        if (invoice.contactId) {
          const lawyerName = globalUser?.displayName || "Lawyer";
          await reSyncInvoiceToClient(globalUser.uid, invoice.id, invoice.contactId, lawyerName);
        }
      }

      toast.success("Invoice marked as unpaid");
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error marking invoice as unpaid:", error);
      toast.error("Failed to mark as unpaid");
    } finally {
      setMarkingPaid(null);
    }
  };

  const fmtDate = (s) =>
    s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  // Derived display status (draft | sent | paid stored; pending/overdue derived).
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const displayStatus = (inv) => {
    if (inv.status === "paid") return "Paid";
    if (inv.status === "draft") return "Draft";
    const due = inv.dueDate ? new Date(inv.dueDate).getTime() : null;
    if (due && due < todayMs) return "Overdue";
    if (inv.status === "sent") return "Sent";
    return "Pending";
  };
  const PILL = {
    Paid: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Sent: "bg-blue-50 text-blue-700 ring-blue-200",
    Pending: "bg-amber-50 text-amber-700 ring-amber-200",
    Overdue: "bg-red-50 text-red-700 ring-red-200",
    Draft: "bg-slate-100 text-slate-600 ring-slate-200",
  };

  const relatedCase = (inv) =>
    inv.selectedCase?.caseNo || (inv.resourceType === "case" ? inv.resourceName : inv.resourceName || "General");

  if (invoices.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center">
        <p className="text-lg text-slate-500">No invoices found</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Invoice No.</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Related Case</th>
                <th className="px-4 py-3">Invoice Date</th>
                <th className="px-4 py-3">Due Date</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((invoice) => {
                const ds = displayStatus(invoice);
                return (
                  <tr
                    key={invoice.id}
                    onClick={() => handleViewInvoice(invoice)}
                    className="cursor-pointer transition-colors hover:bg-slate-50"
                  >
                    <td className="px-4 py-3 align-top font-semibold text-slate-900">
                      {invoice.invoiceNumber}
                    </td>
                    <td className="px-4 py-3 align-top text-slate-700">
                      <div className="max-w-[160px] truncate" title={invoice.contactName}>
                        {invoice.contactName || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-600">{relatedCase(invoice)}</td>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-slate-600">
                      {fmtDate(invoice.invoiceDate || invoice.raisedOn || invoice.createdAt)}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-slate-600">
                      {fmtDate(invoice.dueDate)}
                    </td>
                    <td className="px-4 py-3 align-top text-right font-semibold text-slate-900">
                      {formatAmount(invoice.totalAmount)}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${PILL[ds]}`}>
                        {ds}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewInvoice(invoice)}
                          title="View"
                          className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-blue-600"
                        >
                          <Eye size={16} />
                        </button>
                        {!readOnly &&
                          (markingPaid === invoice.id ? (
                            <span className="p-1.5 text-slate-400">
                              <Loader2 size={16} className="animate-spin" />
                            </span>
                          ) : invoice.status === "paid" ? (
                            <button
                              onClick={() => handleMarkUnpaid(invoice)}
                              title="Mark as Unpaid"
                              className="rounded-lg p-1.5 text-emerald-600 transition-colors hover:bg-emerald-50"
                            >
                              <CheckCircle2 size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleMarkPaid(invoice)}
                              title="Mark as Paid"
                              className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                            >
                              <Circle size={16} />
                            </button>
                          ))}
                        {!readOnly && (
                          <button
                            onClick={() => {
                              if (window.confirm("Are you sure you want to delete this invoice?")) {
                                onDelete(invoice.id);
                              }
                            }}
                            title="Delete"
                            className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-4 text-sm text-slate-500">
        Showing {invoices.length} invoice{invoices.length === 1 ? "" : "s"}
      </p>

      {/* Invoice Details Modal */}
      <InvoiceDetailsView
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        globalUser={globalUser}
        onPaymentCapture={() => {
          if (onRefresh) onRefresh();
          setTimeout(() => {
            setShowDetailsModal(false);
            setSelectedInvoice(null);
          }, 1500);
        }}
        onEdit={onEdit}
        onDelete={(invoiceId) => {
          if (onDelete) onDelete(invoiceId);
        }}
        readOnly={readOnly}
      />
    </>
  );
}

export default InvoiceList;
